import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Spinner, Alert, Modal, ListGroup, InputGroup } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { cookie } from 'utils/cookies';
import { FETCH_STATION_WISE_FORMS, DOWNLOAD_CHECKLIST_FORM, STATUS_CHANGE, CREATE_CHECKLIST, DELETE_CHECKLIST_FORM } from 'actions/formActions';

const StationForms = () => {
    const { stationId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, stationwiseformslist = [], downloadLoading, error, statusLoading, deleteLoading } = useSelector((state) => state.checklist);

    const [search, setSearch] = useState('');
    const [formFilter, setFormFilter] = useState('');
    const [downloadInProgress, setDownloadInProgress] = useState(null);
    const [statusChangeInProgress, setStatusChangeInProgress] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedFormId, setSelectedFormId] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [formName, setFormName] = useState('');
    const [showAddFormsModal, setShowAddFormsModal] = useState(false);
    const [quantities, setQuantities] = useState({});
    const [addFormError, setAddFormError] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(null);

    const userRole = cookie.get('user-role');

    const formNameToComponent = {
        db_panel: 'DBPanelForm',
        apfc_panel: 'APFCPanelForm',
        cable_tray: 'CableTrayForm',
        earthing: 'EarthingForm',
        ht_cable: 'HTCableForm',
        lightning_arrester: 'LightningArresterForm',
        lt_cable: 'LTCableForm',
        lt_panel: 'LTPanelForm',
        transformer: 'TransformerForm',
        vcb: 'VCBForm',
        hoto: 'HOTOForm',
    };

    const checklistItems = [
        'APFC Panel',
        'Cable Tray',
        'DB Panel',
        'Earthing',
        'HT Cable',
        'Lightning Arrester',
        'LT Cable',
        'LT Panel',
        'Transformer',
        'VCB',
        'HOTO (Project Handover Takeover Report)',
    ];

    const formNames = Object.keys(formNameToComponent);

    const fetchStationWiseForms = () => {
        if (stationId) {
            dispatch({ type: FETCH_STATION_WISE_FORMS, payload: stationId });
        }
    };

    useEffect(() => {
        fetchStationWiseForms();
        setQuantities(checklistItems.reduce((acc, item) => ({ ...acc, [item]: 0 }), {}));
    }, [dispatch, stationId, statusLoading]);

    const initiateStatusChange = (formId, formName, newStatus) => {
        setSelectedFormId(formId);
        setSelectedStatus(newStatus);
        setFormName(formName);
        setShowStatusModal(true);
    };

    const handleStatusChange = () => {
        if (selectedFormId) {
            setStatusChangeInProgress(selectedFormId);
            dispatch({
                type: STATUS_CHANGE,
                payload: { formId: selectedFormId, status: selectedStatus, formName },
            });
            if (statusLoading) {
                setStatusChangeInProgress(null);
                toast.success(`Form status changed to ${selectedStatus}`);
                setShowStatusModal(false);
            }
        }
    };

    const initiateDeleteForm = (formId, formName) => {
        setSelectedFormId(formId);
        setFormName(formName);
        setShowDeleteModal(true);
    };

    const handleDeleteForm = () => {
        if (selectedFormId) {
            setDeleteInProgress(selectedFormId);
            dispatch({
                type: DELETE_CHECKLIST_FORM,
                payload: { formId: selectedFormId, formName },
            });
            if (!deleteLoading) {
                fetchStationWiseForms({});
                setShowDeleteModal(false);
            }
        }
    };

    const handleCloseStatusModal = () => {
        setShowStatusModal(false);
        setSelectedFormId(null);
        setSelectedStatus('');
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedFormId(null);
        setFormName('');
    };

    const handleAddFormsModalOpen = () => {
        setShowAddFormsModal(true);
        setAddFormError('');
    };

    const handleAddFormsModalClose = () => {
        setShowAddFormsModal(false);
        setQuantities(checklistItems.reduce((acc, item) => ({ ...acc, [item]: 0 }), {}));
        setAddFormError('');
    };

    const handleQuantityChange = (item, value) => {
        const qty = parseInt(value, 10) || 0;
        setQuantities((prev) => ({ ...prev, [item]: qty }));
        if (addFormError) setAddFormError('');
    };

    const adjustQuantity = (item, delta) => {
        setQuantities((prev) => {
            const updated = Math.max(0, (prev[item] || 0) + delta);
            return { ...prev, [item]: updated };
        });
    };

    const validateAddForms = () => {
        const totalSelected = Object.values(quantities).reduce((sum, qty) => sum + (qty > 0 ? 1 : 0), 0);
        if (totalSelected === 0) {
            setAddFormError('Select at least one form with quantity greater than 0.');
            return false;
        }
        return true;
    };

    const handleAddFormsSubmit = () => {
        if (!validateAddForms()) return;

        const checklist = {};
        Object.entries(quantities).forEach(([item, quantity]) => {
            if (quantity > 0) {
                const key = item
                    .toLowerCase()
                    .replace(/\(.*?\)/g, '')
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_|_$/g, '');
                checklist[key] = quantity;
            }
        });

        const payload = {
            stationId: stationId.toUpperCase(),
            project_name: stationwiseformslist[0]?.project_name || 'Unknown',
            checklist,
            is_existing_station: true,
        };

        dispatch({
            type: CREATE_CHECKLIST,
            payload,
            cb: (res) => {
                if (res.status === 200) {
                    toast.success('Forms added successfully');
                    fetchStationWiseForms();
                    handleAddFormsModalClose();
                } else {
                    setAddFormError(res.data?.message || 'Failed to add forms');
                }
            },
        });
    };

    useEffect(() => {
        if (!downloadLoading) {
            setDownloadInProgress(null);
        }
    }, [downloadLoading]);

    const filteredForms = Array.isArray(stationwiseformslist)
        ? stationwiseformslist.filter((f) => {
            if (!f || typeof f !== 'object') return false;

            const formNameMatch =
                f.formName?.toLowerCase().includes(search.toLowerCase()) ||
                f.userName?.toLowerCase().includes(search.toLowerCase());

            const formFilterMatch =
                !formFilter || f.formName?.toLowerCase() === formFilter.toLowerCase();

            const statusMatch =
                !statusFilter || f.status?.toLowerCase() === statusFilter.toLowerCase();

            const formDate = new Date(f.createdAt);
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;

            const dateInRange =
                (!start || formDate >= start) &&
                (!end || formDate <= new Date(end.setHours(23, 59, 59, 999)));

            return formNameMatch && formFilterMatch && statusMatch && dateInRange;
        })
        : [];

    const handleDownload = (formId, formName, userName) => {
        if (formId) {
            setDownloadInProgress(formId);
            dispatch({
                type: DOWNLOAD_CHECKLIST_FORM,
                payload: { formId, formName, userName, stationId },
                meta: { formId },
            });
        }
    };

    const formatToIST = (utcDate) => {
        if (!utcDate) return 'N/A';
        const date = new Date(utcDate);
        return date.toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });
    };

    const formatStatusForDisplay = (status) => {
        switch (status) {
            case 'approved':
                return 'Approve';
            case 'rejected':
                return 'Reject';
            case 're-work':
                return 'Re-Work';
            case 'submitted':
                return 'Draft';
            default:
                return status || 'N/A';
        }
    };

    const getStatusVariant = (status) => {
        switch (status) {
            case 'approved':
                return 'success';
            case 'rejected':
                return 'danger';
            case 're-work':
                return 'warning';
            case 'pending':
                return 'secondary';
            case 'submitted':
                return 'primary';
            default:
                return 'light';
        }
    };

    return (
        <div className="page-body">
            <div className="container-xl new_application">
                <div className="row mb-3">
                    <div className="col">
                        <h2 className="page-title">Forms for Station: {stationId}</h2>
                    </div>
                    <div className="col-auto">
                        <Button variant="primary" onClick={handleAddFormsModalOpen}>
                            Add Forms
                        </Button>
                    </div>
                </div>

                {error && (
                    <Alert variant="danger">
                        Error fetching forms: {error.message || 'Unknown error'}
                    </Alert>
                )}

                <div className="row mb-3">
                    <div className="col-md-3">
                        <Form.Control
                            type="text"
                            placeholder="Search by User or Form Name"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3">
                        <Form.Select value={formFilter} onChange={(e) => setFormFilter(e.target.value)}>
                            <option value="">Filter by Form Name</option>
                            {formNames.map((name, idx) => (
                                <option key={idx} value={name}>
                                    {name}
                                </option>
                            ))}
                        </Form.Select>
                    </div>
                    <div className="col-md-2">
                        <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="submitted">Draft</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="re-work">Re-Work</option>
                        </Form.Select>
                    </div>
                    <div className="col-md-2">
                        <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div className="col-md-2">
                        <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                </div>

                {loading ? (
                    <div className="text-center my-5">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Table bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Form Name</th>
                                <th>Project Name</th>
                                <th>Version</th>
                                <th>User</th>
                                <th>Status</th>
                                <th>L1</th>
                                <th>L2</th>
                                <th>Created At</th>
                                <th>Updated At</th>
                                <th>Action</th>
                                <th>Download PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredForms.length > 0 ? (
                                filteredForms.map((form) => (
                                    <tr key={form._id}>
                                        <td>{form?.formName || 'N/A'}</td>
                                        <td>{form?.project_name || 'N/A'}</td>
                                        <td>{form?.version || 'N/A'}</td>
                                        <td>{form?.userName || 'N/A'}</td>
                                        <td>
                                            <span className={`badge bg-${getStatusVariant(form?.status)}`}>
                                                {formatStatusForDisplay(form?.status)}
                                            </span>
                                        </td>
                                        <td>{form?.l1?.status || 'N/A'}</td>
                                        <td>{form?.l2?.status || 'N/A'}</td>
                                        <td>{formatToIST(form?.createdAt)}</td>
                                        <td>{formatToIST(form?.updatedAt)}</td>
                                        <td>
                                            {form?.status !== 'approved' && form?.status !== 'rejected' && (
                                                <Button
                                                    variant={form?.status === 'pending' ? 'secondary' : 'primary'}
                                                    size="sm"
                                                    onClick={() => navigate(`/formList/${stationId}/${form?.formName}/${form._id}`)}
                                                    disabled={!form._id}
                                                    className="me-2"
                                                >
                                                    {form?.status === 'pending' ? 'View' : 'Edit'}
                                                </Button>
                                            )}

                                            {userRole === 'admin' && (
                                                <>
                                                    {form?.status === 'approved' && (
                                                        <Button variant="success" size="sm" disabled className="me-2">
                                                            Approved
                                                        </Button>
                                                    )}
                                                    {form?.status === 'rejected' && (
                                                        <Button variant="danger" size="sm" disabled className="me-2">
                                                            Rejected
                                                        </Button>
                                                    )}
                                                    {form?.status === 're-work' && (
                                                        <Button variant="warning" size="sm" disabled className="me-2">
                                                            Rework
                                                        </Button>
                                                    )}
                                                    {form?.status === 'pending' && (
                                                        <>
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={() => initiateStatusChange(form._id, form.formName, 'approved')}
                                                                className="me-2"
                                                            >
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                onClick={() => initiateStatusChange(form._id, form.formName, 'rejected')}
                                                                className="me-2"
                                                            >
                                                                Reject
                                                            </Button>
                                                            <Button
                                                                variant="warning"
                                                                size="sm"
                                                                onClick={() => initiateStatusChange(form._id, form.formName, 're-work')}
                                                                className="me-2"
                                                            >
                                                                Rework
                                                            </Button>
                                                        </>
                                                    )}
                                                    {form?.status !== 'approved' && (
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => initiateDeleteForm(form._id, form.formName)}
                                                            disabled={deleteInProgress === form._id}
                                                            className="me-2"
                                                        >
                                                            Delete
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            <Button
                                                variant="info"
                                                size="sm"
                                                onClick={() => handleDownload(form._id, form?.formName, form?.userName)}
                                                disabled={!form._id || downloadInProgress === form._id}
                                                className="me-2"
                                            >
                                                {downloadInProgress === form._id ? 'Loading...' : 'Download PDF'}
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="11" className="text-center text-muted">
                                        No forms available for this station.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}

                {/* Status Change Confirmation Modal */}
                <Modal show={showStatusModal} onHide={handleCloseStatusModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Status Change</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to {formatStatusForDisplay(selectedStatus)} this form?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseStatusModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleStatusChange}
                            disabled={statusChangeInProgress === selectedFormId}
                        >
                            {statusChangeInProgress === selectedFormId ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Changing...
                                </>
                            ) : (
                                'Confirm'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {`Are you sure you want to delete the form "${formName}"?`}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseDeleteModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDeleteForm}
                            disabled={deleteInProgress === selectedFormId}
                        >
                            {deleteInProgress === selectedFormId ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Deleting...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Add Forms Modal */}
                <Modal show={showAddFormsModal} onHide={handleAddFormsModalClose} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Add Forms to Station: {stationId}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {addFormError && (
                            <Alert variant="danger" className="mb-3">
                                {addFormError}
                            </Alert>
                        )}
                        <ListGroup>
                            {checklistItems.map((item) => (
                                <ListGroup.Item key={item} className="d-flex align-items-center">
                                    <span className="flex-grow-1">{item}</span>
                                    <InputGroup style={{ width: '150px' }}>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => adjustQuantity(item, -1)}
                                            disabled={quantities[item] === 0}
                                        >
                                            −
                                        </Button>
                                        <Form.Control
                                            type="number"
                                            value={quantities[item]}
                                            min={0}
                                            onChange={(e) => handleQuantityChange(item, e.target.value)}
                                            style={{ textAlign: 'center' }}
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => adjustQuantity(item, 1)}
                                        >
                                            +
                                        </Button>
                                    </InputGroup>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleAddFormsModalClose}>
                            Cancel
                        </Button>
                        <Button variant="success" onClick={handleAddFormsSubmit}>
                            Send
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default StationForms;